/**
 * MS Project Service
 * Handles all MS Project data retrieval using Office.js API
 */

/* global console, Office, setTimeout, clearTimeout */
import { API_CONFIG } from "../config/api";
import {
  payloadSnapshot,
  sentryAddBreadcrumb,
  sentryCaptureException,
  sentryCaptureMessage,
  setSentryScope,
} from "./sentry.js";

const PROJECT_TASK_FIELD_FALLBACK_IDS = Object.freeze({
  Marked: 188743751,
  Notes: 188743695,
  ResourceNames: 188743729,
  FinishVariance: 188743726,
  Stop: 188743780,
  Resume: 188743779,
  Text1: 188743731,
  Text2: 188743734,
  Text3: 188743737,
  Text4: 188743740,
  Text5: 188743743,
  Text6: 188743746,
  Text7: 188743747,
  Text8: 188743748,
  Text9: 188743749,
  Text10: 188743750,
  Text11: 188743997,
  Text12: 188743998,
  Text13: 188743999,
  Text14: 188744000,
  Text15: 188744001,
  Text16: 188744002,
  Text17: 188744003,
  Text18: 188744004,
  Text19: 188744005,
  Text20: 188744006,
  Text21: 188744007,
  Text22: 188744008,
  Text23: 188744009,
  Text24: 188744010,
  Text25: 188744011,
  Text26: 188744012,
  Text27: 188744013,
  Text28: 188744014,
  Text29: 188744015,
  Text30: 188744016,
});

const BRIDGE_SYNC_MARKER_FIELD = "Text30";
const BRIDGE_SYNC_MARKER_PREFIX = "GS_SYNC_V1";

const CONSTRAINT_TYPE_LABEL_TO_CODE = Object.freeze({
  "as soon as possible": 0,
  asap: 0,
  "as late as possible": 1,
  alap: 1,
  "must start on": 2,
  mso: 2,
  "must finish on": 3,
  mfo: 3,
  "start no earlier than": 4,
  snet: 4,
  "start no later than": 5,
  snlt: 5,
  "finish no earlier than": 6,
  fnet: 6,
  "finish no later than": 7,
  fnlt: 7,
});

const DATEISH_TASK_FIELD_NAMES = new Set([
  "Start",
  "Finish",
  "BaselineStart",
  "BaselineFinish",
  "ConstraintDate",
  "Deadline",
  "ActualStart",
  "Stop",
  "Resume",
  "ActualFinish",
]);

const DURATION_LIKE_VALUE_PATTERN = /^-?\d+(?:\.\d+)?\s*(?:m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days|w|wk|wks|week|weeks|mo|mon|mons|month|months)$/i;
const UNSAFE_TASK_FIELD_FALLBACK_NAMES = new Set(["Marked", "Stop", "Resume"]);


class MSProjectService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 30000;
  }

  getIdentityMode() {
    const runtimeMode =
      (typeof window !== "undefined" && (
        window.__BACKEND_IDENTITY_MODE__ || window.backendRuntimeConfig?.identity_mode
      )) || null;
    const localOverride =
      (typeof window !== "undefined" && window.localStorage)
        ? window.localStorage.getItem("IDENTITY_MODE")
        : null;
    const mode = String(runtimeMode || localOverride || "dual").trim().toLowerCase();
    return (mode === "legacy" || mode === "dual" || mode === "stable") ? mode : "dual";
  }

  createGeneratedStableId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `gs_${crypto.randomUUID().replace(/-/g, "_")}`;
    }
    return `gs_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  normalizeStableId(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  }

  /**
   * The Text29 a task already carries, flat or nested.
   *
   * The VSTO sidecar nests custom text fields under `textFields` to match the
   * backend's shape, so the flat aliases are empty on that path. Reading only the
   * flat keys made every sidecar-sourced row look unstamped, and callers then
   * coined a GUID-seeded id instead - which Microsoft Project re-mints on Save As,
   * so it cannot pair two versions of one plan.
   */
  readTaskText29(task) {
    if (!task) return "";
    const flat = this.normalizeStableId(task.Text29 || task.text29);
    if (flat) return flat;
    const nested = task.textFields || task.text_fields;
    if (!nested || typeof nested !== "object") return "";
    return this.normalizeStableId(nested.Text29 || nested.text29);
  }

  deriveStableId(rawValue, taskGuid, taskIndex = 0) {
    const normalized = this.normalizeStableId(rawValue);
    if (normalized) return normalized;

    const guidPart = this.cleanGuid(taskGuid);
    if (guidPart) {
      return `gs_${guidPart.toLowerCase().replace(/-/g, "_")}`;
    }

    return `gs_tmp_${taskIndex}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  async setTaskFieldValue(taskGuid, fieldId, value) {
    return new Promise((resolve) => {
      Office.context.document.setTaskFieldAsync(taskGuid, fieldId, value, (result) => resolve(result));
    });
  }

  isBridgeSyncMarker(value) {
    return String(value || "").trim().startsWith(`${BRIDGE_SYNC_MARKER_PREFIX}|`);
  }

  buildBridgeSyncMarker(bridgeSync = {}) {
    const commandId = String(bridgeSync.command_id || "").trim().replace(/[|\r\n]/g, "");
    const commandHash = String(bridgeSync.command_hash || "").trim().replace(/[|\r\n]/g, "");
    if (!commandId) return "";
    return `${BRIDGE_SYNC_MARKER_PREFIX}|command_id=${commandId}|hash=${commandHash}`;
  }

  async getProjectSummaryTaskGuid() {
    if (!Office?.context?.document) {
      return null;
    }

    return new Promise((resolve) => {
      Office.context.document.getTaskByIndexAsync(0, (result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
      });
    });
  }

  async writeBridgeSyncMarker(bridgeSync = {}) {
    if (!Office?.context?.document) {
      return { status: "skipped", reason: "office_context_unavailable" };
    }

    const marker = this.buildBridgeSyncMarker(bridgeSync);
    if (!marker) {
      return { status: "skipped", reason: "missing_command_id" };
    }

    const fieldId = this.getTaskFieldId(BRIDGE_SYNC_MARKER_FIELD);
    if (fieldId === undefined) {
      return { status: "skipped", reason: "marker_field_unavailable" };
    }

    const summaryTaskGuid = await this.getProjectSummaryTaskGuid();
    if (!summaryTaskGuid) {
      return { status: "skipped", reason: "project_summary_task_unavailable" };
    }

    const existingValue = await this.getTaskFieldWithRetry(
      summaryTaskGuid,
      fieldId,
      BRIDGE_SYNC_MARKER_FIELD,
      1
    );
    const normalizedExisting = String(existingValue || "").trim();
    if (normalizedExisting && !this.isBridgeSyncMarker(normalizedExisting)) {
      return { status: "skipped", reason: "marker_field_occupied" };
    }

    if (normalizedExisting === marker) {
      return { status: "unchanged", taskGuid: summaryTaskGuid };
    }

    const writeResult = await this.setTaskFieldValue(summaryTaskGuid, fieldId, marker);
    if (writeResult.status !== Office.AsyncResultStatus.Succeeded) {
      return {
        status: "failed",
        reason: writeResult.error?.message || "marker_write_failed",
      };
    }

    return { status: "written", taskGuid: summaryTaskGuid };
  }

  getTaskFieldId(fieldName) {
    if (!fieldName) return undefined;
    const officialFieldId = Office?.ProjectTaskFields?.[fieldName];
    if (officialFieldId !== undefined) {
      return officialFieldId;
    }
    if (UNSAFE_TASK_FIELD_FALLBACK_NAMES.has(fieldName)) {
      return undefined;
    }
    return PROJECT_TASK_FIELD_FALLBACK_IDS[fieldName];
  }

  normalizeFilterFlagField(flagField) {
    if (!flagField || typeof flagField !== "string") {
      return null;
    }

    const match = flagField.trim().match(/^flag(\d{1,2})$/i);
    if (!match) {
      return null;
    }

    const flagNumber = Number(match[1]);
    if (Number.isNaN(flagNumber) || flagNumber < 1 || flagNumber > 20) {
      return null;
    }

    return `Flag${flagNumber}`;
  }

  getOfficeFlagFieldId(flagField) {
    const normalizedFlagField = this.normalizeFilterFlagField(flagField);
    if (!normalizedFlagField) {
      return undefined;
    }
    return this.getTaskFieldId(normalizedFlagField);
  }

  async ensureStableIdsPresent(tasks = []) {
    if (this.getIdentityMode() === "legacy") {
      return {
        repaired: 0,
        total: Array.isArray(tasks) ? tasks.length : 0,
        warnings: ["IDENTITY_MODE=legacy; stable-id backfill skipped."],
      };
    }

    if (!Array.isArray(tasks) || tasks.length === 0) {
      return { repaired: 0, total: 0, warnings: [] };
    }

    const seen = new Set();
    const warnings = [];
    let repaired = 0;
    const text29FieldId = this.getTaskFieldId("Text29");
    const canWriteText29 = text29FieldId !== undefined;

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i] || {};
      const text29Value = this.readTaskText29(task);
      let stableId = this.normalizeStableId(task.stableId || task.stable_id || text29Value);
      const lowerStableId = stableId.toLowerCase();
      let needsWriteback = false;

      if (!stableId || seen.has(lowerStableId)) {
        stableId = this.deriveStableId("", task.guid || task.id, i);
        repaired++;
        needsWriteback = true;
      }

      // If Text29 was missing (or mismatched), persist the resolved stable id back to MSP.
      if (!text29Value || text29Value !== stableId) {
        needsWriteback = true;
      }

      seen.add(stableId.toLowerCase());
      task.stableId = stableId;
      task.stable_id = stableId;
      task.Text29 = stableId;
      task.text29 = stableId;

      if (canWriteText29 && needsWriteback && task.guid) {
        try {
          const result = await this.setTaskFieldValue(task.guid, text29FieldId, stableId);
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            warnings.push(`Failed writing Text29 for '${task.name || task.uid || i}': ${result.error?.message || "unknown error"}`);
          }
        } catch (error) {
          warnings.push(`Exception writing Text29 for '${task.name || task.uid || i}': ${error.message}`);
        }
      }
    }

    if (!canWriteText29) {
      warnings.push("Text29 is unavailable in Office.ProjectTaskFields; using in-memory stable ids only.");
    }

    return { repaired, total: tasks.length, warnings };
  }

  extractFieldValue(fieldValue) {
    if (fieldValue === null || fieldValue === undefined) return null;

    if (typeof fieldValue === "object" && fieldValue.fieldValue !== undefined) {
      return fieldValue.fieldValue;
    }

    return fieldValue;
  }

  coalesceDefined(...values) {
    for (const value of values) {
      if (value !== undefined && value !== null) {
        return value;
      }
    }
    return null;
  }

  isDurationLikeValue(value) {
    if (value === null || value === undefined) return false;
    const strValue = String(value).trim();
    if (!strValue) return false;
    if (/^P(?:T.*|\d)/i.test(strValue)) return true;
    return DURATION_LIKE_VALUE_PATTERN.test(strValue);
  }

  isDateLikeValue(value) {
    if (value === null || value === undefined || value === "") return false;
    if (value instanceof Date) return true;
    if (typeof value === "number") return false;

    const strValue = String(value).trim();
    if (!strValue) return false;
    const lowered = strValue.toLowerCase();
    if (["na", "n/a", "none", "null"].includes(lowered)) {
      return true;
    }
    if (this.isDurationLikeValue(strValue)) {
      return false;
    }
    if (
      /\d{4}-\d{2}-\d{2}/.test(strValue) ||
      /\b(?:mon|tue|wed|thu|fri|sat|sun)\b/i.test(strValue) ||
      /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(strValue)
    ) {
      return true;
    }
    return !Number.isNaN(Date.parse(strValue));
  }

  extractDateishFieldValue(fieldValue) {
    if (fieldValue === null || fieldValue === undefined) {
      return null;
    }

    if (fieldValue instanceof Date || typeof fieldValue !== "object") {
      return fieldValue;
    }

    const preferredKeys = [
      "text",
      "stringValue",
      "displayValue",
      "formattedValue",
      "value",
      "fieldValue",
    ];
    for (const key of preferredKeys) {
      if (!(key in fieldValue)) {
        continue;
      }
      const candidate = fieldValue[key];
      if (this.isDateLikeValue(candidate)) {
        return candidate;
      }
    }

    if (fieldValue.fieldValue !== undefined) {
      return fieldValue.fieldValue;
    }

    return fieldValue;
  }

  snapshotDebugValue(value, depth = 0) {
    if (value === null || value === undefined) {
      return value;
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof value !== "object") {
      return value;
    }

    if (depth >= 2) {
      if (Array.isArray(value)) {
        return `[Array(${value.length})]`;
      }
      return "[Object]";
    }

    if (Array.isArray(value)) {
      return value.slice(0, 5).map((item) => this.snapshotDebugValue(item, depth + 1));
    }

    const preferredKeys = [
      "fieldValue",
      "text",
      "stringValue",
      "displayValue",
      "formattedValue",
      "value",
      "name",
      "Name",
      "resourceName",
      "resource_name",
      "id",
      "ID",
      "uid",
      "UID",
      "guid",
      "GUID",
      "marked",
      "Marked",
      "isMarked",
      "IsMarked",
      "critical",
      "Critical",
      "isCritical",
      "IsCritical",
      "deadline",
      "Deadline",
      "stop",
      "Stop",
      "stopDate",
      "StopDate",
      "resume",
      "Resume",
      "resumeDate",
      "ResumeDate",
      "actualFinish",
      "ActualFinish",
      "actualFinishDate",
      "ActualFinishDate",
    ];

    const snapshot = {
      __keys: Object.keys(value).slice(0, 20),
    };
    for (const key of preferredKeys) {
      if (!(key in value)) {
        continue;
      }
      snapshot[key] = this.snapshotDebugValue(value[key], depth + 1);
    }
    return snapshot;
  }

  buildTaskFieldDebugSnapshot(fieldName, status, rawValue, extractedValue, error = null) {
    return {
      fieldName,
      status,
      extractedValue: this.snapshotDebugValue(extractedValue),
      rawValue: this.snapshotDebugValue(rawValue),
      error: error ? String(error?.message || error) : null,
    };
  }

  buildTaskObjectDebugSnapshot(taskObj = {}) {
    return {
      keys: Object.keys(taskObj || {}).slice(0, 40),
      direct: {
        marked: this.snapshotDebugValue(taskObj.marked),
        Marked: this.snapshotDebugValue(taskObj.Marked),
        isMarked: this.snapshotDebugValue(taskObj.isMarked),
        IsMarked: this.snapshotDebugValue(taskObj.IsMarked),
        critical: this.snapshotDebugValue(taskObj.critical),
        Critical: this.snapshotDebugValue(taskObj.Critical),
        isCritical: this.snapshotDebugValue(taskObj.isCritical),
        IsCritical: this.snapshotDebugValue(taskObj.IsCritical),
        deadline: this.snapshotDebugValue(taskObj.deadline),
        Deadline: this.snapshotDebugValue(taskObj.Deadline),
        resourceNames: this.snapshotDebugValue(taskObj.resourceNames),
        ResourceNames: this.snapshotDebugValue(taskObj.ResourceNames),
        stop: this.snapshotDebugValue(taskObj.stop),
        Stop: this.snapshotDebugValue(taskObj.Stop),
        stopDate: this.snapshotDebugValue(taskObj.stopDate),
        StopDate: this.snapshotDebugValue(taskObj.StopDate),
        resume: this.snapshotDebugValue(taskObj.resume),
        Resume: this.snapshotDebugValue(taskObj.Resume),
        resumeDate: this.snapshotDebugValue(taskObj.resumeDate),
        ResumeDate: this.snapshotDebugValue(taskObj.ResumeDate),
        actualFinish: this.snapshotDebugValue(taskObj.actualFinish),
        ActualFinish: this.snapshotDebugValue(taskObj.ActualFinish),
        actualFinishDate: this.snapshotDebugValue(taskObj.actualFinishDate),
        ActualFinishDate: this.snapshotDebugValue(taskObj.ActualFinishDate),
      },
      collections: {
        resources: this.snapshotDebugValue(taskObj.resources),
        Resources: this.snapshotDebugValue(taskObj.Resources),
        assignedResources: this.snapshotDebugValue(taskObj.assignedResources),
        assigned_resources: this.snapshotDebugValue(taskObj.assigned_resources),
      },
    };
  }

  extractTaskObjectResourceNames(taskObj) {
    const directValue = this.coalesceDefined(
      taskObj?.resourceNames,
      taskObj?.ResourceNames,
      taskObj?.resource_names
    );
    if (directValue !== null && directValue !== undefined && String(directValue).trim() !== "") {
      return directValue;
    }

    for (const key of ["assignedResources", "assigned_resources", "resources", "Resources"]) {
      const collection = taskObj?.[key];
      if (!Array.isArray(collection) || collection.length === 0) {
        continue;
      }

      const names = collection
        .map((item) => {
          if (item === null || item === undefined) {
            return null;
          }
          if (typeof item === "string") {
            return item.trim();
          }
          return (
            item.name ||
            item.Name ||
            item.resourceName ||
            item.resource_name ||
            item.text ||
            item.fieldValue ||
            null
          );
        })
        .filter((item) => typeof item === "string" && item.trim() !== "")
        .map((item) => item.trim());

      if (names.length > 0) {
        return names.join(";");
      }
    }

    return null;
  }

  normalizeBooleanField(fieldValue, defaultValue = false) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }

    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "number") {
      return value !== 0;
    }

    const lowered = String(value).trim().toLowerCase();
    if (this.isDurationLikeValue(lowered)) {
      return defaultValue;
    }
    if (["yes", "true", "1"].includes(lowered)) {
      return true;
    }
    if (["no", "false", "0"].includes(lowered)) {
      return false;
    }

    return defaultValue;
  }

  normalizeOptionalBooleanField(fieldValue, defaultValue = null) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    if (typeof value === "string" && this.isDurationLikeValue(value)) {
      return defaultValue;
    }
    const normalized = this.normalizeBooleanField(value, false);
    if (typeof value === "string") {
      const lowered = value.trim().toLowerCase();
      if (!["yes", "true", "1", "no", "false", "0"].includes(lowered)) {
        return defaultValue;
      }
    }
    return normalized;
  }

  normalizeOptionalTextField(fieldValue, defaultValue = "") {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined) {
      return defaultValue;
    }
    return String(value);
  }

  normalizeDateField(fieldValue) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined) return "";

    const strValue = String(value).trim();
    if (!strValue) return "";

    const lowered = strValue.toLowerCase();
    if (lowered === "na" || lowered === "n/a" || lowered === "none" || lowered === "null") {
      return "";
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (this.isDurationLikeValue(strValue)) {
      return "";
    }

    if (!this.isDateLikeValue(strValue)) {
      return "";
    }

    return strValue;
  }

  pickNormalizedDateValue(...values) {
    for (const value of values) {
      const normalized = this.normalizeDateField(value);
      if (normalized) {
        return normalized;
      }
    }
    return "";
  }

  pickTaskObjectValue(taskObj, ...keys) {
    if (!taskObj || typeof taskObj !== "object") {
      return null;
    }
    for (const key of keys) {
      if (key in taskObj && taskObj[key] !== undefined && taskObj[key] !== null) {
        return taskObj[key];
      }
    }
    return null;
  }

  normalizeBaselineDuration(fieldValue) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined) return "0d";

    const strValue = String(value).trim();
    return strValue || "0d";
  }

  normalizeConstraintType(fieldValue) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined || value === "") {
      return 0;
    }

    if (typeof value === "number") {
      return value;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return numericValue;
    }

    const normalized = String(value)
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");
    return CONSTRAINT_TYPE_LABEL_TO_CODE[normalized] ?? 0;
  }

  normalizePercentComplete(fieldValue) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined || value === "") {
      return 0;
    }

    if (typeof value === "number") {
      return value;
    }

    const strValue = String(value).replace("%", "").trim();
    const numericValue = Number(strValue);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  }

  normalizeTotalSlack(fieldValue) {
    const value = this.extractFieldValue(fieldValue);
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "number") {
      return value;
    }

    const strValue = String(value).trim();
    return strValue || null;
  }

  normalizeFreeSlack(fieldValue) {
    return this.normalizeTotalSlack(fieldValue);
  }

  getTrackedTextFieldKeys() {
    return Array.from({ length: 30 }, (_, index) => `Text${index + 1}`);
  }

  extractTextFieldsFromTaskPayload(taskData = {}) {
    const textFields = {};
    const candidateMaps = [taskData.text_fields, taskData.textFields];

    for (const candidateMap of candidateMaps) {
      if (!candidateMap || typeof candidateMap !== "object") {
        continue;
      }

      for (const [key, rawValue] of Object.entries(candidateMap)) {
        if (!/^Text([1-9]|[12][0-9]|30)$/.test(key)) {
          continue;
        }
        if (rawValue === null || rawValue === undefined) {
          continue;
        }
        const normalizedValue = String(rawValue);
        if (normalizedValue === "") {
          continue;
        }
        textFields[key] = normalizedValue;
      }
    }

    for (const textKey of this.getTrackedTextFieldKeys()) {
      if (!(textKey in taskData) && !(textKey.toLowerCase() in taskData)) {
        continue;
      }

      const rawValue = taskData[textKey] ?? taskData[textKey.toLowerCase()];
      if (rawValue === null || rawValue === undefined) {
        continue;
      }

      const normalizedValue = String(rawValue);
      if (normalizedValue === "") {
        continue;
      }
      textFields[textKey] = normalizedValue;
    }

    return textFields;
  }

  /**
   * Remove curly braces from GUID string
   * Converts "{8C7D47B8-B684-F011-9F5C-58CE2A1B19AC}" to "8C7D47B8-B684-F011-9F5C-58CE2A1B19AC"
   */
  cleanGuid(guid) {
    if (!guid || typeof guid !== 'string') return guid;
    return guid.replace(/[{}]/g, '');
  }

  processTaskData(taskData) {
    const processedTask = {};

    for (const [key, value] of Object.entries(taskData)) {
      if (typeof value === "object" && value !== null && value.fieldValue !== undefined) {
        processedTask[key] = value.fieldValue;
      } else {
        processedTask[key] = value;
      }
    }

    return processedTask;
  }

  /**
   * Get maximum task index from MS Project
   * @returns {Promise<number>} Maximum task index
   */
  async getMaxTaskIndex() {
    if (!Office || !Office.context || !Office.context.document) {
      return 0;
    }

    return new Promise((resolve) => {
      Office.context.document.getMaxTaskIndexAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          resolve(0);
        }
      });
    });
  }

  /**
   * Get task by index with comprehensive field retrieval
   * @param {number} taskIndex - Task index
   * @returns {Promise<Object>} Task data
   */
  async getTaskByIndex(taskIndex) {
    if (!Office || !Office.context || !Office.context.document) {
      return null;
    }

    try {
      const document = Office.context.document;

      const taskGuid = await new Promise((resolve) => {
        document.getTaskByIndexAsync(taskIndex, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            resolve(null);
          }
        });
      });

      if (!taskGuid) {
        const fallbackStableId = this.deriveStableId("", null, taskIndex);
        return {
          id: taskIndex,
          name: `Task ${taskIndex}`,
          start: null,
          finish: null,
          duration: null,
          percentComplete: 0,
          baselineDuration: "0d",
          baselineStart: "",
          baselineFinish: "",
          constraintDate: "",
          constraintType: 0,
          deadline: null,
          Deadline: null,
          totalSlack: null,
          freeSlack: null,
          finishVariance: null,
          actualStart: "",
          stop: "",
          resume: "",
          actualFinish: "",
          active: true,
          isActive: true,
          marked: null,
          notes: "",
          resourceNames: "",
          textFields: {},
          text_fields: {},
          stableId: fallbackStableId,
          stable_id: fallbackStableId,
          Text29: fallbackStableId,
          text29: fallbackStableId,
          retrieved_at: new Date().toISOString(),
        };
      }

      const taskData = {};
      let completedFields = 0;
      const additionalTextFieldKeys = this.getTrackedTextFieldKeys().filter((key) => key !== "Text29");
      const totalFields = 35 + 4 + additionalTextFieldKeys.length;  // +1 for TaskType (BUG-13)
      let isResolved = false;
      let resolveTask = null;
      let completionTimeout = null;

      const checkCompletion = () => {
        completedFields++;
        console.log(`Completed fields: ${completedFields}/${totalFields}`);

        if (completedFields === totalFields) {
          const processedTaskData = this.processTaskData(taskData);
          const textFields = this.extractTextFieldsFromTaskPayload(processedTaskData);
          const resolvedStableId = this.deriveStableId(processedTaskData.Text29, taskGuid, taskIndex);
          const activeValue = this.normalizeBooleanField(processedTaskData.Active, true);
          const markedValue = this.normalizeOptionalBooleanField(processedTaskData.Marked, null);
          const task = {
            id: processedTaskData.ID || taskIndex,
            name: processedTaskData.Name || `Task ${taskIndex}`,
            start: this.normalizeDateField(processedTaskData.Start) || null,
            finish: this.normalizeDateField(processedTaskData.Finish) || null,
            startRaw: this.normalizeDateField(processedTaskData.Start) || null,
            finishRaw: this.normalizeDateField(processedTaskData.Finish) || null,
            duration: processedTaskData.Duration || null,
            percentComplete: this.normalizePercentComplete(processedTaskData.PercentComplete),
            outlineLevel: processedTaskData.OutlineLevel || 1,
            summary: processedTaskData.Summary || false,
            critical: processedTaskData.Critical || false,
            milestone: processedTaskData.Milestone || false,
            active: activeValue,
            isActive: activeValue,
            hidden: processedTaskData.Hidden || false,
            predecessors: processedTaskData.Predecessors || "",
            successors: processedTaskData.Successors || "",
            cost: processedTaskData.Cost || 0,
            work: processedTaskData.Work || 0,
            baselineDuration: this.normalizeBaselineDuration(processedTaskData.BaselineDuration),
            baselineStart: this.normalizeDateField(processedTaskData.BaselineStart),
            baselineFinish: this.normalizeDateField(processedTaskData.BaselineFinish),
            constraintDate: this.normalizeDateField(processedTaskData.ConstraintDate),
            constraintType: this.normalizeConstraintType(processedTaskData.ConstraintType),
            deadline: this.normalizeDateField(processedTaskData.Deadline),
            Deadline: this.normalizeDateField(processedTaskData.Deadline),
            taskType: processedTaskData.TaskType ?? null,  // BUG-13: Fixed Units=0 / Duration=1 / Work=2
            totalSlack: this.normalizeTotalSlack(processedTaskData.TotalSlack),
            freeSlack: this.normalizeFreeSlack(processedTaskData.FreeSlack),
            finishVariance: this.normalizeTotalSlack(processedTaskData.FinishVariance),
            actualStart: this.normalizeDateField(processedTaskData.ActualStart),
            stop: this.normalizeDateField(processedTaskData.Stop),
            resume: this.normalizeDateField(processedTaskData.Resume),
            actualFinish: this.normalizeDateField(processedTaskData.ActualFinish),
            marked: markedValue,
            notes: this.normalizeOptionalTextField(processedTaskData.Notes, ""),
            resourceNames: this.normalizeOptionalTextField(processedTaskData.ResourceNames, ""),
            textFields: textFields,
            text_fields: textFields,
            actualDuration: processedTaskData.ActualDuration || null,
            actualWork: processedTaskData.ActualWork || 0,
            actualCost: processedTaskData.ActualCost || 0,
            remainingDuration: processedTaskData.RemainingDuration || null,
            remainingWork: processedTaskData.RemainingWork || 0,
            remainingCost: processedTaskData.RemainingCost || 0,
            stableId: resolvedStableId,
            stable_id: resolvedStableId,
            Text29: resolvedStableId,
            text29: resolvedStableId,
            retrieved_at: new Date().toISOString(),
          };
          console.log(
            `Task ${taskIndex} completed with ${completedFields}/${totalFields} fields`
          );
          console.log(`Task ${taskIndex} dates: Start = ${task.start}, Finish = ${task.finish}`);
          console.log(`Task ${taskIndex} successors: ${task.successors}`);
          console.log(`Task ${taskIndex} outline level: ${task.outlineLevel}`);
          console.log(`Task ${taskIndex} is summary: ${task.summary}`);
          if (resolveTask && !isResolved) {
            if (completionTimeout) {
              clearTimeout(completionTimeout);
            }
            isResolved = true;
            resolveTask(task);
          }
          return task;
        }

        return null;
      };

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Name, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Name = this.extractFieldValue(result.value);
        } else {
          taskData.Name = `Task ${taskIndex}`;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ID, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ID = result.value;
        } else {
          taskData.ID = taskIndex;
        }
        checkCompletion();
      });

      Office.context.document.getTaskFieldAsync(
        taskGuid,
        Office.ProjectTaskFields.Start,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Start = this.extractDateishFieldValue(result.value);
          } else {
            taskData.Start = null;
          }
          checkCompletion();
        }
      );

      Office.context.document.getTaskFieldAsync(
        taskGuid,
        Office.ProjectTaskFields.Finish,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Finish = this.extractDateishFieldValue(result.value);
          } else {
            taskData.Finish = null;
          }
          checkCompletion();
        }
      );

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Duration, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Duration = this.extractFieldValue(result.value);
        } else {
          taskData.Duration = null;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.PercentComplete, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.PercentComplete = this.extractFieldValue(result.value);
        } else {
          taskData.PercentComplete = 0;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.BaselineDuration, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.BaselineDuration = this.extractFieldValue(result.value);
        } else {
          taskData.BaselineDuration = "0d";
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.BaselineStart, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.BaselineStart = this.extractDateishFieldValue(result.value);
        } else {
          taskData.BaselineStart = "";
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.BaselineFinish, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.BaselineFinish = this.extractDateishFieldValue(result.value);
        } else {
          taskData.BaselineFinish = "";
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ConstraintDate, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ConstraintDate = this.extractDateishFieldValue(result.value);
        } else {
          taskData.ConstraintDate = "";
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ConstraintType, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ConstraintType = this.extractFieldValue(result.value);
        } else {
          taskData.ConstraintType = 0;
        }
        checkCompletion();
      });

      if (Office?.ProjectTaskFields?.Deadline !== undefined) {
        document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Deadline, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Deadline = this.extractDateishFieldValue(result.value);
          } else {
            taskData.Deadline = null;
          }
          checkCompletion();
        });
      } else {
        taskData.Deadline = null;
        checkCompletion();
      }

      // BUG-13: read the task Type (Fixed Units=0, Fixed Duration=1, Fixed Work=2) so it can
      // be preserved on export instead of always defaulting to Fixed Units.
      if (Office?.ProjectTaskFields?.Type !== undefined) {
        document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Type, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.TaskType = this.extractFieldValue(result.value);
          } else {
            taskData.TaskType = null;
          }
          checkCompletion();
        });
      } else {
        taskData.TaskType = null;
        checkCompletion();
      }

      if (Office?.ProjectTaskFields?.TotalSlack !== undefined) {
        document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.TotalSlack, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.TotalSlack = this.extractFieldValue(result.value);
          } else {
            taskData.TotalSlack = null;
          }
          checkCompletion();
        });
      } else {
        taskData.TotalSlack = null;
        checkCompletion();
      }

      if (Office?.ProjectTaskFields?.FreeSlack !== undefined) {
        document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.FreeSlack, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.FreeSlack = this.extractFieldValue(result.value);
          } else {
            taskData.FreeSlack = null;
          }
          checkCompletion();
        });
      } else {
        taskData.FreeSlack = null;
        checkCompletion();
      }

      Office.context.document.getTaskFieldAsync(
        taskGuid,
        Office.ProjectTaskFields.OutlineLevel,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.OutlineLevel = result.value.fieldValue;
          } else {
            taskData.OutlineLevel = 1;
          }
          checkCompletion();
        }
      );

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Critical, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Critical = result.value;
        } else {
          taskData.Critical = false;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Milestone, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Milestone = result.value;
        } else {
          taskData.Milestone = false;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Active, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Active = this.extractFieldValue(result.value);
        } else {
          taskData.Active = true;
        }
        checkCompletion();
      });

      const markedFieldId = this.getTaskFieldId("Marked");
      if (markedFieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, markedFieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Marked = this.extractFieldValue(result.value);
          } else {
            taskData.Marked = null;
          }
          checkCompletion();
        });
      } else {
        taskData.Marked = null;
        checkCompletion();
      }

      const notesFieldId = this.getTaskFieldId("Notes");
      if (notesFieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, notesFieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Notes = this.extractFieldValue(result.value);
          } else {
            taskData.Notes = "";
          }
          checkCompletion();
        });
      } else {
        taskData.Notes = "";
        checkCompletion();
      }

      const resourceNamesFieldId = this.getTaskFieldId("ResourceNames");
      if (resourceNamesFieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, resourceNamesFieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.ResourceNames = this.extractFieldValue(result.value);
          } else {
            taskData.ResourceNames = "";
          }
          checkCompletion();
        });
      } else {
        taskData.ResourceNames = "";
        checkCompletion();
      }

      const finishVarianceFieldId = this.getTaskFieldId("FinishVariance");
      if (finishVarianceFieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, finishVarianceFieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.FinishVariance = this.extractFieldValue(result.value);
          } else {
            taskData.FinishVariance = null;
          }
          checkCompletion();
        });
      } else {
        taskData.FinishVariance = null;
        checkCompletion();
      }

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Hidden, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Hidden = result.value;
        } else {
          taskData.Hidden = false;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Predecessors, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Predecessors = this.extractFieldValue(result.value);
        } else {
          taskData.Predecessors = "";
        }
        checkCompletion();
      });

      Office.context.document.getTaskFieldAsync(
        taskGuid,
        Office.ProjectTaskFields.Successors,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Successors = result.value.fieldValue;
          } else {
            taskData.Successors = "";
          }
          checkCompletion();
        }
      );

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Cost, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Cost = result.value;
        } else {
          taskData.Cost = 0;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.Work, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.Work = result.value;
        } else {
          taskData.Work = 0;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ActualStart, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ActualStart = this.extractDateishFieldValue(result.value);
        } else {
          taskData.ActualStart = null;
        }
        checkCompletion();
      });

      const stopFieldId = this.getTaskFieldId("Stop");
      if (stopFieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, stopFieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Stop = this.extractDateishFieldValue(result.value);
          } else {
            taskData.Stop = "";
          }
          checkCompletion();
        });
      } else {
        taskData.Stop = "";
        checkCompletion();
      }

      const resumeFieldId = this.getTaskFieldId("Resume");
      if (resumeFieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, resumeFieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Resume = this.extractDateishFieldValue(result.value);
          } else {
            taskData.Resume = "";
          }
          checkCompletion();
        });
      } else {
        taskData.Resume = "";
        checkCompletion();
      }

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ActualFinish, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ActualFinish = this.extractDateishFieldValue(result.value);
        } else {
          taskData.ActualFinish = null;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ActualDuration, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ActualDuration = result.value;
        } else {
          taskData.ActualDuration = null;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ActualWork, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ActualWork = result.value;
        } else {
          taskData.ActualWork = 0;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.ActualCost, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.ActualCost = result.value;
        } else {
          taskData.ActualCost = 0;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.RemainingDuration, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.RemainingDuration = result.value;
        } else {
          taskData.RemainingDuration = null;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.RemainingWork, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.RemainingWork = result.value;
        } else {
          taskData.RemainingWork = 0;
        }
        checkCompletion();
      });

      document.getTaskFieldAsync(taskGuid, Office.ProjectTaskFields.RemainingCost, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          taskData.RemainingCost = result.value;
        } else {
          taskData.RemainingCost = 0;
        }
        checkCompletion();
      });

      const text29FieldId = this.getTaskFieldId("Text29");
      if (text29FieldId !== undefined) {
        document.getTaskFieldAsync(taskGuid, text29FieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData.Text29 = this.extractFieldValue(result.value);
          } else {
            taskData.Text29 = "";
          }
          checkCompletion();
        });
      } else {
        taskData.Text29 = "";
        checkCompletion();
      }

      for (const textKey of additionalTextFieldKeys) {
        const fieldId = this.getTaskFieldId(textKey);
        if (fieldId === undefined) {
          taskData[textKey] = "";
          checkCompletion();
          continue;
        }

        document.getTaskFieldAsync(taskGuid, fieldId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            taskData[textKey] = this.extractFieldValue(result.value);
          } else {
            taskData[textKey] = "";
          }
          checkCompletion();
        });
      }

      return new Promise((resolve) => {
        resolveTask = resolve;

        completionTimeout = setTimeout(() => {
          if (!isResolved) {
            console.warn(
              `Timeout for task ${taskIndex} after 10 seconds, returning partial data`
            );
            isResolved = true;
            const timeoutStableId = this.deriveStableId(taskData.Text29, taskGuid, taskIndex);
            const timeoutTextFields = this.extractTextFieldsFromTaskPayload(taskData);
            const timeoutActive = this.normalizeBooleanField(taskData.Active, true);
            resolve({
              id: taskData.ID || taskIndex,
              name: taskData.Name || `Task ${taskIndex}`,
              start: this.normalizeDateField(taskData.Start) || null,
              finish: this.normalizeDateField(taskData.Finish) || null,
              startRaw: this.normalizeDateField(taskData.Start) || null,
              finishRaw: this.normalizeDateField(taskData.Finish) || null,
              duration: taskData.Duration || null,
              percentComplete: this.normalizePercentComplete(taskData.PercentComplete),
              outlineLevel: taskData.OutlineLevel || 1,
              summary: taskData.Summary || false,
              critical: taskData.Critical || false,
              milestone: taskData.Milestone || false,
              active: timeoutActive,
              isActive: timeoutActive,
              hidden: taskData.Hidden || false,
              predecessors: taskData.Predecessors || "",
              cost: taskData.Cost || 0,
              work: taskData.Work || 0,
              baselineDuration: this.normalizeBaselineDuration(taskData.BaselineDuration),
              baselineStart: this.normalizeDateField(taskData.BaselineStart),
              baselineFinish: this.normalizeDateField(taskData.BaselineFinish),
              constraintDate: this.normalizeDateField(taskData.ConstraintDate),
              constraintType: this.normalizeConstraintType(taskData.ConstraintType),
              deadline: this.normalizeDateField(taskData.Deadline),
              Deadline: this.normalizeDateField(taskData.Deadline),
              totalSlack: this.normalizeTotalSlack(taskData.TotalSlack),
              freeSlack: this.normalizeFreeSlack(taskData.FreeSlack),
              finishVariance: this.normalizeTotalSlack(taskData.FinishVariance),
              actualStart: this.normalizeDateField(taskData.ActualStart),
              stop: this.normalizeDateField(taskData.Stop),
              resume: this.normalizeDateField(taskData.Resume),
              actualFinish: this.normalizeDateField(taskData.ActualFinish),
              marked: this.normalizeOptionalBooleanField(taskData.Marked, null),
              notes: this.normalizeOptionalTextField(taskData.Notes, ""),
              resourceNames: this.normalizeOptionalTextField(taskData.ResourceNames, ""),
              textFields: timeoutTextFields,
              text_fields: timeoutTextFields,
              actualDuration: taskData.ActualDuration || null,
              actualWork: taskData.ActualWork || 0,
              actualCost: taskData.ActualCost || 0,
              remainingDuration: taskData.RemainingDuration || null,
              remainingWork: taskData.RemainingWork || 0,
              remainingCost: taskData.RemainingCost || 0,
              stableId: timeoutStableId,
              stable_id: timeoutStableId,
              Text29: timeoutStableId,
              text29: timeoutStableId,
              retrieved_at: new Date().toISOString(),
              timeout: true,
            });
          }
        }, 10000);
      });
    } catch (error) {
      console.error(`Error getting task ${taskIndex}:`, error);
      const fallbackStableId = this.deriveStableId("", null, taskIndex);
      return {
        id: taskIndex,
        name: `Task ${taskIndex}`,
        start: null,
        finish: null,
        duration: null,
        percentComplete: 0,
        baselineDuration: "0d",
        baselineStart: "",
        baselineFinish: "",
        constraintDate: "",
        constraintType: 0,
        totalSlack: null,
        freeSlack: null,
        actualStart: "",
        stop: "",
        resume: "",
        actualFinish: "",
        stableId: fallbackStableId,
        stable_id: fallbackStableId,
        Text29: fallbackStableId,
        text29: fallbackStableId,
        retrieved_at: new Date().toISOString(),
      };
    }
  }

  /**
   * Get current view information from MS Project
   * @returns {Promise<Object>} View information
   */
  async getCurrentViewInfo() {
    if (!Office || !Office.context || !Office.context.document) {
      return { viewType: "Unknown", viewName: "Current View" };
    }

    try {
      const viewInfo = {
        viewType: "Unknown",
        viewName: "Current View",
        isTaskView: true,
        isResourceView: false,
        isAssignmentView: false,
        retrieved_at: new Date().toISOString(),
      };

      try {
        const maxTaskIndex = await this.getMaxTaskIndex();
        if (maxTaskIndex > 0) {
          viewInfo.isTaskView = true;
          viewInfo.viewType = "Task View";
        }
      } catch {
      }

      try {
        const maxResourceIndex = await this.getMaxResourceIndex();
        if (maxResourceIndex > 0) {
          viewInfo.isResourceView = true;
          viewInfo.viewType = "Resource View";
        }
      } catch {
      }

      return viewInfo;
    } catch (error) {
      console.error("Error getting view info:", error);
      return {
        viewType: "Unknown",
        viewName: "Current View",
        isTaskView: false,
        isResourceView: false,
        isAssignmentView: false,
        error: error.message,
        retrieved_at: new Date().toISOString(),
      };
    }
  }

  /**
   * Helper to fetch a project field with timeout
   * @param {number} [timeoutMs=2000] - Timeout in milliseconds
   * @returns {Promise<any>} Field value or null
   */
  async getProjectFieldWithTimeout(fieldId, fieldName, timeoutMs = 2000) {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn(`Timeout fetching project property '${fieldName}' - skipping`);
        resolve(null);
      }, timeoutMs);

      try {
        Office.context.document.getProjectFieldAsync(fieldId, {}, (result) => {
          clearTimeout(timeoutId);
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value.fieldValue !== undefined ? result.value.fieldValue : result.value);
          } else {
            console.warn(`Failed to fetch project property '${fieldName}':`, result.error);
            resolve(null);
          }
        });
      } catch (e) {
        clearTimeout(timeoutId);
        console.warn(`Exception fetching project property '${fieldName}':`, e);
        resolve(null);
      }
    });
  }

  /**
   * Get project properties from MS Project
   * @returns {Promise<Object>} Project properties
   */
  async getProjectProperties() {
    if (!Office || !Office.context || !Office.context.document) {
      return { name: "Unknown Project" };
    }
    console.log("Starting to fetch project properties (Sequential)...");
    const properties = {};

    try {
      // Fetch properties SEQUENTIALLY with targeted timeouts
      // Core fields need reliability (5s)
      // Optional/Server fields should fail fast (1.5s) to avoid UI blocking

      const guid = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.GUID, "GUID", 5000);
      if (guid) properties.projectGuid = this.cleanGuid(guid);

      const start = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.Start, "Start", 5000);
      if (start) properties.start = start;

      const finish = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.Finish, "Finish", 5000);
      if (finish) properties.finish = finish;

      const currencyDigits = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.CurrencyDigits, "CurrencyDigits", 1500);
      if (currencyDigits) properties.currencyDigits = currencyDigits;

      const currencySymbol = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.CurrencySymbol, "CurrencySymbol", 1500);
      if (currencySymbol) properties.currencySymbol = currencySymbol;

      const currencySymbolPosition = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.CurrencySymbolPosition, "CurrencySymbolPosition", 1500);
      if (currencySymbolPosition) properties.currencySymbolPosition = currencySymbolPosition;

      const durationUnits = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.DurationUnits, "DurationUnits", 1500);
      if (durationUnits) properties.durationUnits = durationUnits;

      const workUnits = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.WorkUnits, "WorkUnits", 1500);
      if (workUnits) properties.workUnits = workUnits;

      const readOnly = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.ReadOnly, "ReadOnly", 2000);
      if (readOnly !== null) properties.readOnly = readOnly;

      const projectServerUrl = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.ProjectServerUrl, "ProjectServerUrl", 2000);
      if (projectServerUrl) properties.projectServerUrl = projectServerUrl;

      const wssUrl = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.WSSUrl, "WSSUrl", 1500);
      if (wssUrl) properties.wssUrl = wssUrl;

      const wssList = await this.getProjectFieldWithTimeout(Office.ProjectProjectFields.WSSList, "WSSList", 1500);
      if (wssList) properties.wssList = wssList;

      properties.name = "Project";

      console.log("Finished fetching project properties.");
    } catch (error) {
      console.error("Error fetching project properties:", error);
    }

    return properties;
  }

  /**
   * Get comprehensive project data from MS Project
   * @returns {Promise<Object>} Complete project data
   */
  async getProjectData(options = {}) {
    try {
      if (!Office || !Office.context || !Office.context.document) {
        throw new Error("Office context not available - cannot access MS Project data");
      }

      const properties = await this.getProjectProperties();

      let tasks = [];
      let filterSyncPrefetch = null;
      let extractionDiagnostics = null;

      try {
        const tasksBundle = await this.getAllTasksWithDetailsBundle({
          plannedFilterFlagField: options.plannedFilterFlagField || null,
        });
        tasks = tasksBundle.tasks;
        filterSyncPrefetch = tasksBundle.filterSyncPrefetch;
        extractionDiagnostics = tasksBundle.extractionDiagnostics;
        console.log(`Retrieved ${tasks.length} tasks using comprehensive method`);
      } catch (taskError) {
        console.warn(
          "Comprehensive task retrieval failed, falling back to basic method:",
          taskError
        );

        const maxTaskIndex = await this.getMaxTaskIndex();
        if (maxTaskIndex > 0) {
          for (let i = 0; i <= maxTaskIndex; i++) {
            try {
              const task = await this.getTaskByIndex(i);
              if (task) {
                tasks.push(task);
              }
            } catch (error) {
              console.warn(`Failed to get task ${i}:`, error);
            }
          }
        }
      }

      if (tasks.length === 0) {
        console.log("No tasks found in MS Project");
      }

      const projectId = `ms_project_${Date.now()}`;

      const result = {
        project_id: projectId,
        properties: properties,
        tasks: tasks,
        assignments: [],
        filter_sync_prefetch: filterSyncPrefetch,
        metadata: {
          extractedAt: new Date().toISOString(),
          totalTasks: tasks.length,
          method: tasks.length > 0 ? "comprehensive" : "fallback",
          extractionDiagnostics,
        },
      };

      console.log(`Project data retrieved: ${tasks.length} tasks`);
      return result;
    } catch (error) {
      console.error("Error getting project data:", error);
      return {
        project_id: "error_project",
        properties: { name: "Error Project" },
        tasks: [],
        resources: [],
        assignments: [],
        metadata: {
          extractedAt: new Date().toISOString(),
          error: error.message,
        },
      };
    }
  }

  /**
   * Get all tasks with details
   * @returns {Promise<Array>} Array of task objects
   */
  /**
   * Helper to fetch a task field with retry logic and timeout
   * @param {string} taskId - Task GUID
   * @param {Object} fieldId - Office.ProjectTaskFields enum
   * @param {string} fieldName - Human readable field name for logging
   * @param {number} retries - Number of retries
   * @returns {Promise<any>} Field value or null
   */
  async getTaskFieldWithRetry(taskId, fieldId, fieldName, retries = 1, debugSink = null) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error("Timeout"));
          }, 3000); // 3s timeout (was 10s×3 — too slow for Office.js fallback)

          Office.context.document.getTaskFieldAsync(taskId, fieldId, (result) => {
            clearTimeout(timeoutId);
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const extractedValue = DATEISH_TASK_FIELD_NAMES.has(fieldName)
                ? this.extractDateishFieldValue(result.value)
                : this.extractFieldValue(result.value);
              if (typeof debugSink === "function") {
                debugSink(
                  this.buildTaskFieldDebugSnapshot(
                    fieldName,
                    "succeeded",
                    result.value,
                    extractedValue
                  )
                );
              }
              resolve(extractedValue);
            } else {
              if (typeof debugSink === "function" && attempt === retries) {
                debugSink(
                  this.buildTaskFieldDebugSnapshot(
                    fieldName,
                    "failed",
                    result?.value,
                    null,
                    result?.error
                  )
                );
              }
              reject(result.error);
            }
          });
        });
      } catch (error) {
        if (attempt === retries) {
          console.error(`Failed to fetch '${fieldName}' for task ${taskId} after ${retries} attempts:`, error);
          return null; // Give up and return null to prevent hanging
        }
        console.warn(`Retry ${attempt}/${retries} fetching '${fieldName}' for task ${taskId}:`, error);
        // Wait a bit before retrying
        await new Promise(r => setTimeout(r, 500 * attempt));
      }
    }
    return null;
  }

  async getTaskFieldWithRetryByName(taskId, fieldName, retries = 3, debugSink = null) {
    const fieldId = this.getTaskFieldId(fieldName);
    if (fieldId === undefined) {
      if (typeof debugSink === "function") {
        debugSink(
          this.buildTaskFieldDebugSnapshot(
            fieldName,
            "unsupported",
            null,
            null,
            "unsupported_field_for_office_js_host"
          )
        );
      }
      return null;
    }
    return this.getTaskFieldWithRetry(taskId, fieldId, fieldName, retries, debugSink);
  }

  /**
   * Get all tasks with details using robust sequential fetching
   * @returns {Promise<Array>} Array of task objects
   */
  async getAllTasksWithDetailsBundle(options = {}) {
    return this.getAllTasksWithDetailsPromise({
      ...options,
      returnBundle: true,
    });
  }

  async getAllTasksWithDetailsPromise(options = {}) {
    console.log("Starting to fetch all tasks (Robust Sequential version)...");

    try {
      const plannedFilterFlagField = this.normalizeFilterFlagField(
        options.plannedFilterFlagField
      );
      const officePlannedFilterFlagField = this.getOfficeFlagFieldId(plannedFilterFlagField);
      const filterSyncPrefetch = plannedFilterFlagField
        ? {
            planned_flag_field: plannedFilterFlagField,
            by_guid: {},
            total_tasks: 0,
            read_failures: 0,
            complete: officePlannedFilterFlagField !== undefined,
            exhausted: false,
            source: "fresh_fetch",
          }
        : null;
      const maxIndex = await this.getMaxTaskIndex();
      console.log(`Found ${maxIndex + 1} tasks in project`);

      if (maxIndex < 0) {
        const emptyDiagnostics = {
          max_task_index: maxIndex,
          expected_index_count: 0,
          retrieved_task_count: 0,
          task_id_read_failures: [],
          task_processing_errors: [],
          missing_retrieved_indices: [],
          complete: true,
        };
        if (options.returnBundle) {
          return {
            tasks: [],
            filterSyncPrefetch,
            extractionDiagnostics: emptyDiagnostics,
          };
        }
        return [];
      }

      const tasks = [];
      const retrievedIndices = new Set();
      const taskIdReadFailures = [];
      const taskProcessingErrors = [];
      const batchSize = 25; // Optimized: 25 tasks/batch for faster loading (was 10)

      for (let i = 0; i <= maxIndex; i += batchSize) {
        const endIndex = Math.min(i + batchSize, maxIndex + 1);
        console.log(`Processing batch ${Math.ceil((i + 1) / batchSize)}/${Math.ceil((maxIndex + 1) / batchSize)} (Tasks ${i} to ${endIndex - 1})...`);

        // Process tasks in the batch SEQUENTIALLY to avoid 5100 errors
        for (let j = i; j < endIndex; j++) {
          try {
            // 1. Get Task ID
            const taskId = await new Promise((resolve) => {
              Office.context.document.getTaskByIndexAsync(j, (result) => {
                resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : null);
              });
            });

            if (!taskId) {
              console.error(`Failed to get ID for task at index ${j}`);
              taskIdReadFailures.push(j);
              continue;
            }

            // 2. Get Basic Task Object
            const taskObj = await new Promise((resolve) => {
              Office.context.document.getTaskAsync(taskId, (result) => {
                resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : {});
              });
            });
            const officeFieldDebug = {
              taskGuid: taskId,
              taskObject: this.buildTaskObjectDebugSnapshot(taskObj),
              fieldReads: {},
            };

            // 3. Fetch all other fields SEQUENTIALLY
            const name = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Name, "Name");
            const duration = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Duration, "Duration");
            const start = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Start, "Start");
            const finish = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Finish, "Finish");
            const percentComplete = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.PercentComplete, "PercentComplete");
            const baselineDuration = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.BaselineDuration, "BaselineDuration");
            const baselineStart = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.BaselineStart, "BaselineStart");
            const baselineFinish = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.BaselineFinish, "BaselineFinish");
            const constraintDate = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.ConstraintDate, "ConstraintDate");
            const constraintType = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.ConstraintType, "ConstraintType");
            const active = Office?.ProjectTaskFields?.Active !== undefined
              ? await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Active, "Active")
              : taskObj.isActive;
            const marked = await this.getTaskFieldWithRetryByName(
              taskId,
              "Marked",
              3,
              (snapshot) => { officeFieldDebug.fieldReads.Marked = snapshot; }
            );
            const notes = await this.getTaskFieldWithRetryByName(taskId, "Notes");
            const resourceNames = await this.getTaskFieldWithRetryByName(
              taskId,
              "ResourceNames",
              3,
              (snapshot) => { officeFieldDebug.fieldReads.ResourceNames = snapshot; }
            );
            const finishVariance = await this.getTaskFieldWithRetryByName(taskId, "FinishVariance");
            const totalSlack = Office?.ProjectTaskFields?.TotalSlack !== undefined
              ? await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.TotalSlack, "TotalSlack")
              : null;
            const freeSlack = Office?.ProjectTaskFields?.FreeSlack !== undefined
              ? await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.FreeSlack, "FreeSlack")
              : null;
            const critical = Office?.ProjectTaskFields?.Critical !== undefined
              ? await this.getTaskFieldWithRetry(
                taskId,
                Office.ProjectTaskFields.Critical,
                "Critical",
                3,
                (snapshot) => { officeFieldDebug.fieldReads.Critical = snapshot; }
              )
              : taskObj.isCritical;
            const deadline = Office?.ProjectTaskFields?.Deadline !== undefined
              ? await this.getTaskFieldWithRetry(
                taskId,
                Office.ProjectTaskFields.Deadline,
                "Deadline",
                3,
                (snapshot) => { officeFieldDebug.fieldReads.Deadline = snapshot; }
              )
              : await this.getTaskFieldWithRetryByName(
                taskId,
                "Deadline",
                1,
                (snapshot) => { officeFieldDebug.fieldReads.Deadline = snapshot; }
              );
            const actualStart = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.ActualStart, "ActualStart");
            const stop = await this.getTaskFieldWithRetryByName(
              taskId,
              "Stop",
              3,
              (snapshot) => { officeFieldDebug.fieldReads.Stop = snapshot; }
            );
            const resume = await this.getTaskFieldWithRetryByName(
              taskId,
              "Resume",
              3,
              (snapshot) => { officeFieldDebug.fieldReads.Resume = snapshot; }
            );
            const actualFinish = await this.getTaskFieldWithRetry(
              taskId,
              Office.ProjectTaskFields.ActualFinish,
              "ActualFinish",
              3,
              (snapshot) => { officeFieldDebug.fieldReads.ActualFinish = snapshot; }
            );
            const predecessors = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Predecessors, "Predecessors");
            const successors = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Successors, "Successors");
            const outlineLevel = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.OutlineLevel, "OutlineLevel");
            const summary = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.Summary, "Summary");
            const id = await this.getTaskFieldWithRetry(taskId, Office.ProjectTaskFields.ID, "ID"); // Integer ID
            const stableId = await this.getTaskFieldWithRetryByName(taskId, "Text29");
            let plannedFilterFlagValue = null;
            if (filterSyncPrefetch && officePlannedFilterFlagField !== undefined) {
              plannedFilterFlagValue = await this.getTaskFieldWithRetry(
                taskId,
                officePlannedFilterFlagField,
                plannedFilterFlagField
              );
              if (plannedFilterFlagValue === null || plannedFilterFlagValue === undefined) {
                filterSyncPrefetch.read_failures += 1;
                filterSyncPrefetch.complete = false;
              }
              filterSyncPrefetch.by_guid[taskId] = this.normalizeBooleanField(
                plannedFilterFlagValue,
                false
              );
            }
            const textFields = {};
            for (const textKey of this.getTrackedTextFieldKeys()) {
              if (textKey === "Text29") {
                continue;
              }
              const textValue = await this.getTaskFieldWithRetryByName(taskId, textKey);
              if (textValue === null || textValue === undefined || textValue === "") {
                continue;
              }
              textFields[textKey] = String(textValue);
            }
            const resolvedStableId = this.deriveStableId(stableId, taskId, j);
            textFields.Text29 = resolvedStableId;
            const taskObjActive = this.coalesceDefined(
              taskObj.isActive,
              taskObj.active,
              taskObj.Active
            );
            const activeValue = this.normalizeBooleanField(
              this.coalesceDefined(active, taskObjActive),
              taskObjActive !== undefined && taskObjActive !== null ? taskObjActive : true
            );
            const markedSource = this.coalesceDefined(
              taskObj.marked,
              taskObj.Marked,
              taskObj.isMarked,
              taskObj.IsMarked,
              marked
            );
            const notesSource = this.coalesceDefined(taskObj.notes, taskObj.Notes, notes);
            const resourceNamesSource = this.coalesceDefined(
              this.extractTaskObjectResourceNames(taskObj),
              resourceNames
            );
            const baselineStartValue = this.pickNormalizedDateValue(
              baselineStart,
              this.pickTaskObjectValue(taskObj, "baselineStart", "BaselineStart")
            );
            const baselineFinishValue = this.pickNormalizedDateValue(
              baselineFinish,
              this.pickTaskObjectValue(taskObj, "baselineFinish", "BaselineFinish")
            );
            const constraintDateValue = this.pickNormalizedDateValue(
              constraintDate,
              this.pickTaskObjectValue(taskObj, "constraintDate", "ConstraintDate")
            );
            const actualStartValue = this.pickNormalizedDateValue(
              actualStart,
              this.pickTaskObjectValue(taskObj, "actualStart", "ActualStart", "actualStartDate", "ActualStartDate")
            );
            const stopValue = this.pickNormalizedDateValue(
              stop,
              this.pickTaskObjectValue(taskObj, "stop", "Stop", "stopDate", "StopDate")
            );
            const resumeValue = this.pickNormalizedDateValue(
              resume,
              this.pickTaskObjectValue(taskObj, "resume", "Resume", "resumeDate", "ResumeDate")
            );
            const actualFinishValue = this.pickNormalizedDateValue(
              actualFinish,
              this.pickTaskObjectValue(taskObj, "actualFinish", "ActualFinish", "actualFinishDate", "ActualFinishDate")
            );
            const constraintTypeValue = this.normalizeConstraintType(
              this.coalesceDefined(
                constraintType,
                this.pickTaskObjectValue(taskObj, "constraintType", "ConstraintType")
              )
            );
            const criticalValue = this.normalizeBooleanField(
              this.coalesceDefined(
                critical,
                this.pickTaskObjectValue(taskObj, "isCritical", "IsCritical", "critical", "Critical")
              ),
              false
            );
            const deadlineValue = this.pickNormalizedDateValue(
              deadline,
              this.pickTaskObjectValue(taskObj, "deadline", "Deadline")
            );
            const startValue = this.pickNormalizedDateValue(
              start,
              this.pickTaskObjectValue(taskObj, "start", "Start")
            );
            const finishValue = this.pickNormalizedDateValue(
              finish,
              this.pickTaskObjectValue(taskObj, "finish", "Finish")
            );

            tasks.push({
              index: j,
              id: this.cleanGuid(taskId), // This is the CLEANED GUID (no braces)
              guid: taskId, // This is the RAW GUID (with braces) - required for API calls!
              uid: id, // This is the Integer ID (matches backend)
              name: name || taskObj.name || `Task ${j}`,
              stableId: resolvedStableId,
              stable_id: resolvedStableId,
              Text29: resolvedStableId,
              text29: resolvedStableId,
              start: startValue,
              finish: finishValue,
              startRaw: startValue,
              finishRaw: finishValue,
              percentComplete: this.normalizePercentComplete(
                percentComplete !== null ? percentComplete : taskObj.percentComplete
              ),
              isCritical: criticalValue,
              critical: criticalValue,
              duration: duration,
              baselineDuration: this.normalizeBaselineDuration(baselineDuration),
              baselineStart: baselineStartValue,
              baselineFinish: baselineFinishValue,
              constraintDate: constraintDateValue,
              constraintType: constraintTypeValue,
              deadline: deadlineValue,
              Deadline: deadlineValue,
              active: activeValue,
              isActive: activeValue,
              marked: this.normalizeOptionalBooleanField(markedSource, null),
              notes: this.normalizeOptionalTextField(notesSource, ""),
              resourceNames: this.normalizeOptionalTextField(resourceNamesSource, ""),
              finishVariance: this.normalizeTotalSlack(finishVariance),
              textFields: textFields,
              text_fields: textFields,
              totalSlack: this.normalizeTotalSlack(totalSlack),
              freeSlack: this.normalizeFreeSlack(freeSlack),
              actualStart: actualStartValue,
              stop: stopValue,
              resume: resumeValue,
              actualFinish: actualFinishValue,
              predecessors: predecessors,
              successors: successors,
              cost: taskObj.cost?.value || taskObj.cost?.amount || taskObj.cost || 0,
              work: taskObj.work?.value || taskObj.work?.amount || taskObj.work || 0,
              outlineLevel: outlineLevel !== null ? outlineLevel : (taskObj.outlineLevel || 1),
              isSummary: summary !== null ? summary : (taskObj.isSummary || false),
              isMilestone: taskObj.isMilestone || false,
              isHidden: taskObj.isHidden || false,
              officeFieldDebug,
              retrieved_at: new Date().toISOString(),
            });
            retrievedIndices.add(j);

          } catch (err) {
            console.error(`Error processing task ${j}:`, err);
            taskProcessingErrors.push({
              index: j,
              message: err?.message || String(err),
            });
          }
        }

        // Small delay between batches to let Office.js breathe
        if (endIndex <= maxIndex) {
          await new Promise(r => setTimeout(r, 100));
        }
      }

      console.log(`Finished fetching all tasks. Total: ${tasks.length}`);
      if (filterSyncPrefetch) {
        filterSyncPrefetch.total_tasks = tasks.length;
      }
      const missingRetrievedIndices = [];
      for (let index = 0; index <= maxIndex; index++) {
        if (!retrievedIndices.has(index) && !taskIdReadFailures.includes(index)) {
          missingRetrievedIndices.push(index);
        }
      }
      const extractionDiagnostics = {
        max_task_index: maxIndex,
        expected_index_count: maxIndex + 1,
        retrieved_task_count: tasks.length,
        task_id_read_failures: taskIdReadFailures,
        task_processing_errors: taskProcessingErrors,
        missing_retrieved_indices: missingRetrievedIndices,
        has_unresolved_index_slots: taskIdReadFailures.length > 0 || missingRetrievedIndices.length > 0,
        complete: taskProcessingErrors.length === 0,
      };
      if (options.returnBundle) {
        return {
          tasks,
          filterSyncPrefetch,
          extractionDiagnostics,
        };
      }
      return tasks;
    } catch (error) {
      console.error("Critical error in getAllTasksWithDetailsPromise:", error);
      throw error;
    }
  }


  /**
   * Apply a filter flag to tasks in the live MS Project document.
   * Prefers the already-fetched chat snapshot and planned-flag prefetch, with a
   * one-column live flag read fallback if the prefetched flag state is missing
   * or invalid.
   * @param {Object} options
   * @param {Array} options.filteredTasks - Tasks returned from the backend filter result
   * @param {string} options.targetFlagField - The flag field to write into
   * @param {string|null} [options.sourceFlagField=null] - Backend source flag field used to identify matches
   * @param {string|null} [options.plannedFlagField=null] - Planned frontend target flag field for this request
   * @param {Array|null} [options.allTasksSnapshot=null] - Optional pre-fetched current project tasks
   * @param {Object|null} [options.prefetchedFlagState=null] - Prefetched frontend-only live flag state
   * @returns {Promise<Object>} Structured sync result summary
   */
  async applyFilterFlags(options = {}) {
    const {
      filteredTasks = [],
      targetFlagField = "Flag10",
      sourceFlagField = null,
      plannedFlagField = null,
      allTasksSnapshot = null,
      prefetchedFlagState = null,
    } = options;

    if (!Array.isArray(filteredTasks)) {
      return {
        total_tasks_considered: 0,
        matched_tasks_expected: 0,
        matched_tasks_found: 0,
        writes_attempted: 0,
        writes_succeeded: 0,
        writes_failed: 0,
        used_snapshot: false,
        fallback_reason: "invalid_filtered_tasks",
        planned_flag_field: this.normalizeFilterFlagField(plannedFlagField),
        source_flag_field: this.normalizeFilterFlagField(sourceFlagField),
        prefetch_used: false,
        prefetch_fallback_used: false,
        prefetch_fallback_reason: null,
        current_yes_count: 0,
        desired_yes_count: 0,
        writes_set_yes: 0,
        writes_set_no: 0,
        writes_skipped: 0,
      };
    }

    return this.applyFilterFlagsWithSnapshot({
      filteredTasks,
      targetFlagField,
      sourceFlagField,
      plannedFlagField,
      allTasksSnapshot,
      prefetchedFlagState,
    });
  }

  normalizeFilterRowId(value) {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  normalizeTextFieldName(fieldName) {
    if (!fieldName || typeof fieldName !== "string") return null;
    const match = fieldName.trim().match(/^text(\d{1,2})$/i);
    if (!match) return null;

    const fieldNumber = Number(match[1]);
    if (Number.isNaN(fieldNumber) || fieldNumber < 1 || fieldNumber > 30) {
      return null;
    }

    const normalized = `Text${fieldNumber}`;
    if (normalized === "Text29" || normalized === "Text30") {
      return null;
    }
    return normalized;
  }

  normalizeTasksForFilterSync(tasks = []) {
    if (!Array.isArray(tasks)) {
      return { tasks: [], fallbackReason: "snapshot_missing" };
    }

    if (tasks.length === 0) {
      return { tasks: [], fallbackReason: "snapshot_empty" };
    }

    const normalizedTasks = [];

    for (const task of tasks) {
      const guid = task?.guid || "";
      if (!guid) {
        return { tasks: [], fallbackReason: "snapshot_missing_guids" };
      }

      normalizedTasks.push({
        guid,
        name: (task?.name || "").trim(),
        stableId: this.normalizeStableId(
          task?.stableId || task?.stable_id || task?.Text29 || task?.text29 || ""
        ).toLowerCase(),
        rowId: this.normalizeFilterRowId(task?.row_id ?? task?.rowId ?? task?.ID),
      });
    }

    return { tasks: normalizedTasks, fallbackReason: null };
  }

  normalizeClassificationAssignments(assignments = []) {
    const normalized = [];
    if (!Array.isArray(assignments)) {
      return normalized;
    }

    for (const assignment of assignments) {
      const label = String(assignment?.class || assignment?.label || "").trim();
      if (!label) {
        continue;
      }

      normalized.push({
        label,
        stableId: this.normalizeStableId(
          assignment?.stable_id || assignment?.stableId || assignment?.stableID
        ).toLowerCase(),
        rowId: this.normalizeFilterRowId(
          assignment?.row_id ?? assignment?.rowId ?? assignment?.ID
        ),
        name: (assignment?.name || "").trim(),
      });
    }

    return normalized;
  }

  async readFlagStateForTasks(syncTasks = [], flagField = "Flag10") {
    const normalizedFlagField = this.normalizeFilterFlagField(flagField);
    const officeFlagField = this.getOfficeFlagFieldId(normalizedFlagField);
    const result = {
      planned_flag_field: normalizedFlagField,
      by_guid: {},
      total_tasks: Array.isArray(syncTasks) ? syncTasks.length : 0,
      read_failures: 0,
      complete: Boolean(normalizedFlagField && officeFlagField !== undefined),
      exhausted: false,
      source: "live_flag_column_read",
    };

    if (!normalizedFlagField || officeFlagField === undefined) {
      result.read_failures = result.total_tasks;
      result.complete = false;
      return result;
    }

    for (const task of syncTasks) {
      const taskGuid = task?.guid || "";
      if (!taskGuid) {
        result.read_failures += 1;
        result.complete = false;
        continue;
      }

      const currentFlagValue = await this.getTaskFieldWithRetry(
        taskGuid,
        officeFlagField,
        normalizedFlagField
      );

      if (currentFlagValue === null || currentFlagValue === undefined) {
        result.read_failures += 1;
        result.complete = false;
      }

      result.by_guid[taskGuid] = this.normalizeBooleanField(currentFlagValue, false);
    }

    return result;
  }

  async applyFilterFlagsWithSnapshot({
    filteredTasks = [],
    targetFlagField = "Flag10",
    sourceFlagField = null,
    plannedFlagField = null,
    allTasksSnapshot = null,
    prefetchedFlagState = null,
  } = {}) {
    if (!filteredTasks || !Array.isArray(filteredTasks)) {
      return {
        total_tasks_considered: 0,
        matched_tasks_expected: 0,
        matched_tasks_found: 0,
        writes_attempted: 0,
        writes_succeeded: 0,
        writes_failed: 0,
        used_snapshot: false,
        fallback_reason: "invalid_filtered_tasks",
        planned_flag_field: this.normalizeFilterFlagField(plannedFlagField || targetFlagField),
        source_flag_field: this.normalizeFilterFlagField(sourceFlagField),
        prefetch_used: false,
        prefetch_fallback_used: false,
        prefetch_fallback_reason: null,
        current_yes_count: 0,
        desired_yes_count: 0,
        writes_set_yes: 0,
        writes_set_no: 0,
        writes_skipped: 0,
      };
    }

    const normalizedTargetFlagField =
      this.normalizeFilterFlagField(targetFlagField) || "Flag10";
    const normalizedSourceFlagField = this.normalizeFilterFlagField(sourceFlagField);
    const normalizedPlannedFlagField =
      this.normalizeFilterFlagField(plannedFlagField || normalizedTargetFlagField) ||
      normalizedTargetFlagField;
    const officeFlagField =
      this.getOfficeFlagFieldId(normalizedTargetFlagField) || Office.ProjectTaskFields.Flag10;

    console.log(`Applying ${normalizedTargetFlagField} to ${filteredTasks.length} tasks...`);

    const resultSummary = {
      total_tasks_considered: 0,
      matched_tasks_expected: filteredTasks.length,
      matched_tasks_found: 0,
      writes_attempted: 0,
      writes_succeeded: 0,
      writes_failed: 0,
      used_snapshot: false,
      fallback_reason: null,
      planned_flag_field: normalizedPlannedFlagField,
      source_flag_field: normalizedSourceFlagField,
      prefetch_used: false,
      prefetch_fallback_used: false,
      prefetch_fallback_reason: null,
      current_yes_count: 0,
      desired_yes_count: 0,
      writes_set_yes: 0,
      writes_set_no: 0,
      writes_skipped: 0,
    };

    try {
      let syncTasks = [];
      let normalizationResult = this.normalizeTasksForFilterSync(allTasksSnapshot);

      if (normalizationResult.fallbackReason) {
        resultSummary.fallback_reason = normalizationResult.fallbackReason;
        console.warn(
          `Filter sync snapshot unavailable (${normalizationResult.fallbackReason}). Falling back to live Office.js task fetch.`
        );

        const liveTasks = await this.getAllTasksWithDetailsPromise();
        normalizationResult = this.normalizeTasksForFilterSync(liveTasks);

        if (normalizationResult.fallbackReason) {
          throw new Error(
            `Unable to build a valid task sync source after fallback (${normalizationResult.fallbackReason}).`
          );
        }
      } else {
        resultSummary.used_snapshot = true;
      }

      syncTasks = normalizationResult.tasks;
      resultSummary.total_tasks_considered = syncTasks.length;

      const targetStableSet = new Set(
        filteredTasks
          .map((t) => this.normalizeStableId(t?.stable_id || t?.stableId || t?.stableID))
          .filter(Boolean)
          .map((item) => item.toLowerCase())
      );
      const targetRowIdSet = new Set(
        filteredTasks
          .map((t) => this.normalizeFilterRowId(t?.row_id ?? t?.rowId ?? t?.ID))
          .filter((item) => item !== null)
      );
      const targetNameSet = new Set(
        filteredTasks
          .map((t) => (t?.name || "").trim())
          .filter(Boolean)
      );
      const hasStableTargets = targetStableSet.size > 0;
      const hasRowTargets = targetRowIdSet.size > 0;

      console.log(`Target Stable IDs to flag (First 5):`, Array.from(targetStableSet).slice(0, 5));
      console.log(`Target Row IDs to flag (First 5):`, Array.from(targetRowIdSet).slice(0, 5));
      console.log(`Target Names to flag (First 5):`, Array.from(targetNameSet).slice(0, 5));

      let resolvedFlagState = null;
      const prefetchedFlagMatchesTarget =
        prefetchedFlagState &&
        typeof prefetchedFlagState === "object" &&
        this.normalizeFilterFlagField(prefetchedFlagState.planned_flag_field) ===
          normalizedTargetFlagField &&
        prefetchedFlagState.complete === true &&
        prefetchedFlagState.by_guid &&
        typeof prefetchedFlagState.by_guid === "object";

      if (
        prefetchedFlagMatchesTarget &&
        syncTasks.every((task) => Object.prototype.hasOwnProperty.call(prefetchedFlagState.by_guid, task.guid))
      ) {
        resolvedFlagState = prefetchedFlagState;
        resultSummary.prefetch_used = true;
      } else {
        resultSummary.prefetch_fallback_used = true;
        resultSummary.prefetch_fallback_reason = prefetchedFlagMatchesTarget
          ? "prefetch_missing_task_guids"
          : "prefetch_missing_or_mismatched";
        resolvedFlagState = await this.readFlagStateForTasks(syncTasks, normalizedTargetFlagField);
        if (!resolvedFlagState.complete) {
          throw new Error(
            `Unable to read current ${normalizedTargetFlagField} state for diff-based filter sync.`
          );
        }
      }

      const desiredYesSet = new Set();
      const currentYesSet = new Set();
      const tasksToSetYes = [];
      const tasksToSetNo = [];

      for (const task of syncTasks) {
        const taskStableId = task.stableId;
        const byStableId = Boolean(taskStableId && targetStableSet.has(taskStableId));
        const byRowId = task.rowId !== null && targetRowIdSet.has(task.rowId);
        const byName = targetNameSet.has(task.name);
        let shouldBeYes = byName;
        if (hasStableTargets) {
          shouldBeYes = byStableId;
        } else if (hasRowTargets) {
          shouldBeYes = byRowId;
        }
        const isCurrentlyYes = this.normalizeBooleanField(
          resolvedFlagState.by_guid?.[task.guid],
          false
        );

        if (shouldBeYes) {
          desiredYesSet.add(task.guid);
        }
        if (isCurrentlyYes) {
          currentYesSet.add(task.guid);
        }

        if (shouldBeYes && !isCurrentlyYes) {
          tasksToSetYes.push(task);
        } else if (!shouldBeYes && isCurrentlyYes) {
          tasksToSetNo.push(task);
        }
      }

      resultSummary.matched_tasks_found = desiredYesSet.size;
      resultSummary.current_yes_count = currentYesSet.size;
      resultSummary.desired_yes_count = desiredYesSet.size;

      for (const task of tasksToSetYes) {
        const writeResult = await this.setTaskFieldValue(task.guid, officeFlagField, true);
        resultSummary.writes_attempted++;
        resultSummary.writes_set_yes++;

        if (writeResult.status === Office.AsyncResultStatus.Succeeded) {
          resultSummary.writes_succeeded++;
        } else {
          resultSummary.writes_failed++;
          console.error(
            `Failed to set ${normalizedTargetFlagField}=true for task ${task.name} (Target: ${task.guid}):`,
            writeResult.error
          );
        }
      }

      for (const task of tasksToSetNo) {
        const writeResult = await this.setTaskFieldValue(task.guid, officeFlagField, false);
        resultSummary.writes_attempted++;
        resultSummary.writes_set_no++;

        if (writeResult.status === Office.AsyncResultStatus.Succeeded) {
          resultSummary.writes_succeeded++;
        } else {
          resultSummary.writes_failed++;
          console.error(
            `Failed to set ${normalizedTargetFlagField}=false for task ${task.name} (Target: ${task.guid}):`,
            writeResult.error
          );
        }
      }

      resultSummary.writes_skipped =
        Math.max(resultSummary.total_tasks_considered - resultSummary.writes_attempted, 0);
      console.log(
        `Finished applying ${normalizedTargetFlagField}. Desired yes=${resultSummary.desired_yes_count}, current yes=${resultSummary.current_yes_count}, set yes=${resultSummary.writes_set_yes}, set no=${resultSummary.writes_set_no}, skipped=${resultSummary.writes_skipped}.`
      );
      console.log("Filter sync result summary:", resultSummary);

      return resultSummary;
    } catch (e) {
      console.error("Error applying filter flags:", e);
      throw e;
    }
  }

  async applyTextFieldLabelsWithSnapshot({
    assignments = [],
    textField = "Text28",
    allTasksSnapshot = null,
    clearUnassigned = true,
  } = {}) {
    const normalizedTextField = this.normalizeTextFieldName(textField) || "Text28";
    const officeTextField = this.getTaskFieldId(normalizedTextField);
    const normalizedAssignments = this.normalizeClassificationAssignments(assignments);

    const resultSummary = {
      text_field: normalizedTextField,
      total_tasks_considered: 0,
      labels_expected: normalizedAssignments.length,
      labels_found: 0,
      writes_attempted: 0,
      writes_succeeded: 0,
      writes_failed: 0,
      writes_set_label: 0,
      writes_cleared: 0,
      writes_skipped: 0,
      used_snapshot: false,
      fallback_reason: null,
      clear_unassigned: Boolean(clearUnassigned),
    };

    if (officeTextField === undefined) {
      throw new Error(`${normalizedTextField} is unavailable in this Office.js host.`);
    }

    let normalizationResult = this.normalizeTasksForFilterSync(allTasksSnapshot);
    if (normalizationResult.fallbackReason) {
      resultSummary.fallback_reason = normalizationResult.fallbackReason;
      const liveTasks = await this.getAllTasksWithDetailsPromise();
      normalizationResult = this.normalizeTasksForFilterSync(liveTasks);
      if (normalizationResult.fallbackReason) {
        throw new Error(
          `Unable to build a valid task sync source after fallback (${normalizationResult.fallbackReason}).`
        );
      }
    } else {
      resultSummary.used_snapshot = true;
    }

    const syncTasks = normalizationResult.tasks;
    resultSummary.total_tasks_considered = syncTasks.length;

    const labelsByStableId = new Map();
    const labelsByRowId = new Map();
    const labelsByName = new Map();

    for (const assignment of normalizedAssignments) {
      if (assignment.stableId) labelsByStableId.set(assignment.stableId, assignment.label);
      if (assignment.rowId !== null) labelsByRowId.set(assignment.rowId, assignment.label);
      if (assignment.name) labelsByName.set(assignment.name, assignment.label);
    }

    for (const task of syncTasks) {
      let label = null;
      if (task.stableId && labelsByStableId.has(task.stableId)) {
        label = labelsByStableId.get(task.stableId);
      } else if (task.rowId !== null && labelsByRowId.has(task.rowId)) {
        label = labelsByRowId.get(task.rowId);
      } else if (labelsByName.has(task.name)) {
        label = labelsByName.get(task.name);
      }

      const hasLabel = label !== null && label !== undefined && String(label).trim() !== "";
      if (!hasLabel && !clearUnassigned) {
        resultSummary.writes_skipped++;
        continue;
      }

      const desiredValue = hasLabel ? String(label).trim() : "";
      const currentValue = await this.getTaskFieldWithRetry(
        task.guid,
        officeTextField,
        normalizedTextField,
        1
      );

      if (String(currentValue || "") === desiredValue) {
        resultSummary.writes_skipped++;
        if (hasLabel) resultSummary.labels_found++;
        continue;
      }

      const writeResult = await this.setTaskFieldValue(task.guid, officeTextField, desiredValue);
      resultSummary.writes_attempted++;
      if (hasLabel) {
        resultSummary.writes_set_label++;
        resultSummary.labels_found++;
      } else {
        resultSummary.writes_cleared++;
      }

      if (writeResult.status === Office.AsyncResultStatus.Succeeded) {
        resultSummary.writes_succeeded++;
      } else {
        resultSummary.writes_failed++;
        console.error(
          `Failed to set ${normalizedTextField}=${desiredValue} for task ${task.name}:`,
          writeResult.error
        );
      }
    }

    console.log("Classification label sync result summary:", resultSummary);
    return resultSummary;
  }

  /**
   * Apply Live Updates to task properties (Duration, Start, Finish, PercentComplete)
   * @param {Array} updates - List of {row_id, field, value, task_name} updates from backend
   * @returns {Promise<number>} Number of successful updates
   */
  async applyLiveUpdates(updates) {
    if (!updates || !Array.isArray(updates) || updates.length === 0) {
      console.log("No live updates to apply");
      return 0;
    }

    console.log(`Applying ${updates.length} live updates...`);

    // Map field names to Office.js ProjectTaskFields enum
    const fieldMap = {
      'Duration': Office.ProjectTaskFields.Duration,
      'Start': Office.ProjectTaskFields.Start,
      'Finish': Office.ProjectTaskFields.Finish,
      'PercentComplete': Office.ProjectTaskFields.PercentComplete,
      'Name': Office.ProjectTaskFields.Name,
      'Predecessors': Office.ProjectTaskFields.Predecessors,
      'Successors': Office.ProjectTaskFields.Successors
    };

    // Get all tasks to map row_id to GUID
    const allTasks = await this.getAllTasksWithDetailsPromise();

    // Create lookup maps
    const nameToGuidMap = new Map();
    const idToGuidMap = new Map(); // Map integer UID (row_id) to GUID

    allTasks.forEach(task => {
      if (task.guid) {
        if (task.name) {
          nameToGuidMap.set(task.name.toLowerCase(), task.guid);
        }
        if (task.uid !== undefined && task.uid !== null) {
          idToGuidMap.set(task.uid, task.guid);
          // Also alias string version just in case
          idToGuidMap.set(String(task.uid), task.guid);
        }
      }
    });

    let successCount = 0;

    for (const update of updates) {
      try {
        const { field, value, task_name, row_id } = update;

        let taskGuid = null;

        // 1. Try lookup by row_id (Most reliable, unique)
        if (row_id !== undefined && row_id !== null) {
          taskGuid = idToGuidMap.get(row_id) || idToGuidMap.get(String(row_id));
          if (taskGuid) {
            console.log(`Matched task by row_id ${row_id} -> ${taskGuid}`);
          }
        }

        // 2. Fallback to name lookup (Unreliable if duplicates exist)
        if (!taskGuid && task_name) {
          console.warn(`Lookup by row_id ${row_id} failed, falling back to name '${task_name}'`);
          taskGuid = nameToGuidMap.get(task_name.toLowerCase());
        }

        if (!taskGuid) {
          console.warn(`Task not found for live update: ID=${row_id}, Name='${task_name}'`);
          continue;
        }

        const officeField = fieldMap[field];
        if (!officeField) {
          console.warn(`Unknown field for live update: ${field}`);
          continue;
        }

        // Convert value if needed
        let finalValue = value;
        if (field === 'PercentComplete') {
          finalValue = parseInt(value, 10);
        }
        // For Duration, MS Project expects PT format or number
        // Office.js may accept human-readable format like "5d"

        // Apply the update
        await new Promise((resolve) => {
          Office.context.document.setTaskFieldAsync(taskGuid, officeField, finalValue, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log(`✓ Updated ${task_name}: ${field} = ${value}`);
              successCount++;
            } else {
              console.error(`Failed to update ${task_name} ${field}:`, result.error);
            }
            resolve(result);
          });
        });

      } catch (e) {
        console.error(`Error applying update:`, e);
      }
    }

    console.log(`Live updates complete. ${successCount}/${updates.length} successful.`);
    return successCount;
  }




}

export const msProjectService = new MSProjectService();
export default msProjectService;
